import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component, EventEmitter,
    HostBinding, HostListener,
    Inject,
    Input,
    OnInit, Output,
    PLATFORM_ID
} from '@angular/core';
import {DomSanitizer, SafeStyle} from '@angular/platform-browser';
import {AuthService} from '@core/services/auth.service';
import {isPlatformBrowser} from '@angular/common';
import {Constants} from '@app/app.constants';
import {Router} from '@angular/router';
import {first} from 'rxjs/operators';

@Component({
    selector: 'app-video-thumbnail',
    templateUrl: './video-thumbnail.component.html',
    styleUrls: ['./video-thumbnail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false
})
export class VideoThumbnailComponent implements OnInit {

    // Inputs.
    @Input() video: Api.Video | null = null;
    @Input() type: 'large' | 'medium' | 'small' = 'small';
    @Input() direction: 'vertical' | 'horizontal' = 'vertical';
    @Input() showDeleteButton: boolean = false;

    // Outputs.
    @Output() onDeletePressed = new EventEmitter<Api.Video>();

    // Host binding.
    @HostBinding('class') get hostClass() {
        return `video--${this.direction}`;
    }

    // The background image for the video.
    videoBackground: SafeStyle = null;

    constructor(private _router: Router,
                private _auth: AuthService,
                private _sanitizer: DomSanitizer,
                private _changeDetection: ChangeDetectorRef,
                @Inject(PLATFORM_ID) private platformId: object) {
    }

    ngOnInit() {
        this.loadThumbnail();
    }

    @HostListener('click', ['$event'])
    async onClick() {
        if (!this.video) {
            return;
        }

        const account = await this._auth.accountFromCache
            .pipe(first())
            .toPromise();

        // If the user has access navigate to the video.
        if (this.video.accessible || (account && account.role === 'platform_admin')) {
            return this.navigateToVideo(this.video.id);
        }

        // If the account exists the user needs to go to
        // the subscribe page; else the login page.
        return account
            ? this.navigateToSubscribe()
            : this.navigateToLogin();
    }

    private loadThumbnail() {
        if (!this.video || !this.video.thumbnail_url && !this.video.feed_thumbnail) {
            this.videoBackground = this._sanitizer.bypassSecurityTrustStyle(`url(${Constants.assets.empty})`);
            return;
        }

        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const image = new Image();
        image.src = this.video.feed_thumbnail || this.video.thumbnail_url;

        image.onload = () => {
            // Set the new background image.
            this.videoBackground = this._sanitizer.bypassSecurityTrustStyle(`url(${image.src})`);

            // Mark for check.
            this._changeDetection.markForCheck();
        };
    }

    onDelete(event, video: Api.Video) {
        event.stopPropagation();

        return this.onDeletePressed.emit(video);
    }

    private async navigateToVideo(videoId: string) {
        return await this._router.navigate(['/watch'], {
            queryParams: {
                v: videoId
            }
        });
    }

    private async navigateToSubscribe() {
        return await this._router.navigate(['/settings/change/subscription']);
    }

    private async navigateToLogin() {
        return await this._router.navigate(['/auth/login']);
    }

}
